// Shared by the Docs, Sheets and Slides home screens. The three apps are
// separate Vite builds with no shared package for this, so every file in
// features/home/ except homeConfig.ts is copied verbatim between them —
// edit one, copy it to the other two.

import type { ComponentType } from "react";
import type { DriveItem } from "@/api/driveApi";

export type FileKind = "doc" | "sheet" | "slide" | "pdf" | "image" | "other";

const KIND_PATTERNS: ReadonlyArray<readonly [FileKind, RegExp]> = [
    ["doc", /\.(docx?|rtf|sfdt|txt|odt)$/i],
    ["sheet", /\.(xlsx?|csv|ods)$/i],
    ["slide", /\.(pptx?|odp)$/i],
    ["pdf", /\.pdf$/i],
    ["image", /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic)$/i]
];

export function classify(name: string, contentType?: string | null): FileKind {
    for (const [kind, re] of KIND_PATTERNS) {
        if (re.test(name)) return kind;
    }
    return contentType?.startsWith("image/") ? "image" : "other";
}

/** A file on the home screen — a Drive item plus, for "Shared with me", who shared it. */
export interface HomeItem extends DriveItem {
    sharedBy?: string;
    sharedAt?: string;
}

/** Router state the home screen hands to the editor route (`navigate(path, { state })`). */
export interface EditorLaunchState {
    /** Picked on the home screen: the picker needs the click's user activation. */
    localFile?: File;
    /** Id of one of the app's built-in templates — the offline fallback (see homeConfig.ts). */
    template?: string;
}

/** Which suite editor a shared template opens in (the Drive BFF's `app`). */
export type TemplateApp = "docs" | "sheets" | "slides";

/**
 * One entry of the suite's shared template library (Drive BFF
 * `GET /api/templates`) — the same list Drive's "New" gallery shows.
 * Picking one copies it into My Drive and opens the copy.
 */
export interface SuiteTemplate {
    id: string;
    name: string;
    app: TemplateApp;
    fileName: string;
    category: string;
    /** "#rrggbb" — the template's own colour, used by the drawn thumbnail. */
    accent: string;
    /** Which miniature to draw: blank | letter | invoice | resume | doc | report | grid | grid-chart | slide-*. */
    thumb: string;
    description: string;
    blank: boolean;
    /** e.g. "Healthcare"; blanks and older catalogues are "General business". */
    industry: string;
}

/** The industry a template without one (older catalogue, blank files) falls under. */
export const GENERAL_INDUSTRY = "General business";

/** The shared template library, normalised: every template has an industry. */
export interface SuiteCatalogue {
    templates: SuiteTemplate[];
    /** Non-blank industries in catalogue order (plus any a template names that the list missed). */
    industries: string[];
}

export type RawSuiteTemplate = Omit<SuiteTemplate, "industry"> & { industry?: string | null };

export function normalizeCatalogue(r: { templates?: RawSuiteTemplate[]; industries?: unknown } | null | undefined): SuiteCatalogue {
    const templates = (r?.templates ?? []).map((t) => ({
        ...t,
        industry: typeof t.industry === "string" && t.industry.trim() ? t.industry.trim() : GENERAL_INDUSTRY
    }));
    const industries: string[] = [];
    const add = (s: unknown) => {
        const v = typeof s === "string" ? s.trim() : "";
        if (v && !industries.includes(v)) industries.push(v);
    };
    if (Array.isArray(r?.industries)) r.industries.forEach(add);
    templates.forEach((t) => add(t.industry));
    return { templates, industries };
}

export type PreviewBlock =
    | "title" | "subtitle" | "heading" | "text" | "short" | "bullet" | "table" | "chart" | "gap";

export interface HomeTemplate {
    id: string;
    name: string;
    description: string;
    /** Drawn as a stylised thumbnail — no rendering of the real content. */
    preview: { blocks: PreviewBlock[]; accent: string };
}

export interface DriveDialogProps {
    onClose: () => void;
    onPick: (item: { id: string; name: string }) => void;
}

export interface HomeConfig {
    /** Namespace for the view preferences kept in localStorage. */
    appId: string;
    noun: string;
    nounPlural: string;
    /** File kind this app edits — the default filter. */
    kind: "doc" | "sheet" | "slide";
    /** Names the editor can open. */
    openable: RegExp;
    /** Name substrings that find this app's files through /api/search ("Owned by me"). */
    searchTerms: string[];
    /** `accept` for the "From this computer" picker. */
    localAccept: string;
    editorPath: string;
    /** Adds an "All files" scope (Docs). */
    allFilesOption: boolean;
    /** Tailwind classes — solid tile, soft tint, accent text. */
    accent: { solid: string; soft: string; text: string };
    /** Shape of the template thumbnails. */
    thumb: "page" | "sheet" | "slide";
    /** This app's slice of the shared template library. */
    templateApp: TemplateApp;
    /** Built-in samples — shown only when the shared library can't load. */
    templates: HomeTemplate[];
    /** The editor's own Open-from-Drive dialog. */
    DriveOpenDialog: ComponentType<DriveDialogProps>;
}

// ── Formatting ───────────────────────────────────────────────────────────

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 365 * 24 * 3600],
    ["month", 30 * 24 * 3600],
    ["week", 7 * 24 * 3600],
    ["day", 24 * 3600],
    ["hour", 3600],
    ["minute", 60]
];

export function relativeTime(iso: string | null | undefined): string {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const seconds = Math.round((then - Date.now()) / 1000);
    if (Math.abs(seconds) < 60) return "just now";
    for (const [unit, size] of UNITS) {
        if (Math.abs(seconds) >= size || unit === "minute") {
            return RELATIVE.format(Math.round(seconds / size), unit);
        }
    }
    return "";
}

export type SortKey = "opened" | "name" | "modified";

/** "Opened 3 hours ago" / "Modified yesterday" — what the card's time line says. */
export function timeLabel(item: HomeItem, sort: SortKey): string {
    if (sort !== "modified") {
        if (item.lastAccessedAt) return `Opened ${relativeTime(item.lastAccessedAt)}`;
        if (item.sharedAt) return `Shared ${relativeTime(item.sharedAt)}`;
    }
    return `Modified ${relativeTime(item.updatedAt)}`;
}

export function ownerLabel(item: HomeItem): string {
    return item.ownerEmail ? item.ownerEmail : "me";
}

export function isShared(item: HomeItem): boolean {
    return Boolean(item.ownerEmail);
}

function openedKey(item: HomeItem): string {
    return item.lastAccessedAt ?? item.sharedAt ?? item.updatedAt;
}

export function sortItems(items: HomeItem[], sort: SortKey): HomeItem[] {
    const copy = [...items];
    if (sort === "name") copy.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }));
    else if (sort === "modified") copy.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    else copy.sort((a, b) => openedKey(b).localeCompare(openedKey(a)));
    return copy;
}

/**
 * Keeps the file's extension when the user types a bare name ("Q3 plan" →
 * "Q3 plan.docx") — a rename must never change what format the bytes are in.
 */
export function withExtension(typed: string, current: string): string {
    const trimmed = typed.trim();
    if (!trimmed) throw new Error("Enter a name.");
    const ext = current.match(/\.([^./\\]+)$/)?.[1];
    if (!ext) return trimmed;
    if (trimmed.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
        if (trimmed.length > ext.length + 1) return trimmed;
        throw new Error("Enter a name.");
    }
    return `${trimmed}.${ext}`;
}

export function errorText(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

// ── Preferences (per browser, per app) ──────────────────────────────────

export function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
    try {
        const value = window.localStorage.getItem(key);
        return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
    } catch {
        return fallback;
    }
}

/** A free-form stored preference (validated by the caller); null when unset or storage is off. */
export function readPrefText(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

export function writePref(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* storage disabled — the preference just doesn't stick */
    }
}

// ── Suite URLs (same env keys + fallbacks as the app launcher) ──────────

function env(key: string, fallback: string): string {
    const raw = (import.meta.env as Record<string, string | undefined>)[key];
    return (raw && raw.trim().length > 0 ? raw : fallback).replace(/\/$/, "");
}

export const SUITE_URLS = {
    doc: env("VITE_DOCS_WEB_URL", "http://localhost:5210"),
    sheet: env("VITE_SHEET_WEB_URL", "http://localhost:5220"),
    slide: env("VITE_PRESENT_WEB_URL", "http://localhost:5232"),
    pdf: env("VITE_PDF_WEB_URL", "http://localhost:5250"),
    drive: env("VITE_DRIVE_WEB_URL", "http://localhost:5240")
} as const;

/** The item's folder in Drive when it's the user's own; Drive's home otherwise. */
export function driveUrlFor(item: HomeItem): string {
    if (!isShared(item) && item.parentId) return `${SUITE_URLS.drive}/folder/${encodeURIComponent(item.parentId)}`;
    return `${SUITE_URLS.drive}/`;
}

/** Where a file this app can't edit opens — the sister app that can, else Drive. */
export function externalUrlFor(item: HomeItem): string {
    const kind = classify(item.name, item.contentType);
    if (kind === "doc" || kind === "sheet" || kind === "slide" || kind === "pdf") {
        // `/?fileId=` is the launch shape every suite editor accepts.
        return `${SUITE_URLS[kind]}/?fileId=${encodeURIComponent(item.id)}`;
    }
    return driveUrlFor(item);
}
